import { QueryClient, useQuery, useMutation } from '@tanstack/react-query-v4';
import apiFetch, { HTTPError, defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import { tryParseErrorResponse } from '@scale/frontend-lib/errorMessages';
import {
  IOnboardingFlow,
  IOnboardingFlowRevision,
  IOnboardingFlowDraft,
  SimpleStepStatsRequest,
  SimpleStepStatsResponse,
  StepStatsResponse,
  IUserPathData,
  IUserPathMetrics,
  FlowStatsResponse,
  OnboardingFlowType,
} from '@scale/shared/onboardingControlPanel';
import isemail from 'isemail';
import chunk from 'lodash/chunk';
import { maybeCleanOutlierEmail } from '@scale/shared/email';
import { WorkerTeam as IWorkerTeam } from '@scale/shared/workerTeams/types';
import {
  AddUserToFlowBody,
  AddUserToFlowErrorBody,
  AddUserToFlowErrorType,
  PodType,
  OnboardingFlowEvent,
  IWorkerSkill,
  ISignupFlow,
  SignupFlowRequest,
  ResumeMetadataFields,
  IExtendedSignupFlow,
  JobDetailPanelData,
  SignupFlowType,
  ResumeQualificationSignalField,
  LinkedInQualificationSignalField,
  FastLaneListResponse,
  ListScreeningConfigsResponse,
  FastLaneChainResponse,
} from '@scale/shared-types';
import { ITagJson } from '@scale/shared/types/tag';
import { IProjectJson } from '@scale/shared/types/project';
import { METADATA_VALUES_DISPLAY_PAGE_SIZE } from 'react-scale/components/GrowthCenter/ResumeSearch/constants';

export interface SignupFlowResponse {
  signupFlow: ISignupFlow;
  emails: string[];
  workerTeam: IWorkerTeam;
  tags: ITagJson[];
  userCount: number;
  intendedProjects: IProjectJson[];
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

export async function getFlowById(flowId: string) {
  return await apiFetch<IOnboardingFlow>(null, `corp-api/onboarding-control-panel/flow/${flowId}`, {
    method: 'GET',
  });
}

export async function getFlowEvents(flowId: string) {
  return await apiFetch<OnboardingFlowEvent[]>(
    null,
    `corp-api/onboarding-control-panel/flow/${flowId}/events`,
    {
      method: 'GET',
    },
  );
}

export async function saveDraft(
  flow: IOnboardingFlowDraft<string>,
  originalFlowId?: string,
  signupFlowDraft?: Partial<IExtendedSignupFlow>,
) {
  return await apiFetch<IOnboardingFlowDraft<string>>(
    null,
    `corp-api/onboarding-control-panel/flow/draft`,
    {
      method: 'PUT',
      body: originalFlowId
        ? { draft: flow, originalFlowId, signupFlowDraft }
        : { draft: flow, signupFlowDraft },
    },
  );
}

export async function getDraft(originalFlowId: string) {
  const response = await apiFetch<{
    draft: IOnboardingFlowDraft<string>;
    signupFlowDraft?: ISignupFlow;
  }>(null, `corp-api/onboarding-control-panel/flow/draft/${originalFlowId}`, {
    method: 'GET',
  });

  return response;
}

export async function getVersion(flowId: string, version: number) {
  return await apiFetch<IOnboardingFlowRevision<string>>(
    null,
    `corp-api/onboarding-control-panel/flow/version/${flowId}/${version}`,
    {
      method: 'GET',
    },
  );
}

export async function publishDraft(
  draftFlow: IOnboardingFlowDraft<string>,
  unsavedSignupFlow?: Partial<IExtendedSignupFlow>,
) {
  return await apiFetch<{
    flow: IOnboardingFlow;
    draft: IOnboardingFlowDraft<string>;
    signupFlow: ISignupFlow;
  }>(null, `corp-api/onboarding-control-panel/flow/draft/publish`, {
    method: 'PUT',
    body: { draftFlow, unsavedSignupFlow },
  });
}

export async function findOCPWorkflows() {
  const allFlows = await apiFetch<IOnboardingFlow[]>(
    null,
    `corp-api/onboarding-control-panel/flow?type=${OnboardingFlowType.Growth}`,
  );
  return allFlows;
}

export async function findOCPWorkflowsWithUserCount() {
  const allFlows = await apiFetch<IOnboardingFlow[]>(
    null,
    `corp-api/onboarding-control-panel/flow?type=${OnboardingFlowType.Growth}&withUserCount=true`,
  );
  return allFlows;
}

export async function findWorkerTeamNameById(teamId: string) {
  const workerTeam = await apiFetch<IWorkerTeam>(null, `corp-api/worker_teams/team/${teamId}`);
  return workerTeam.name;
}

export async function findSignupFlowsForOnboardingFlow(flowId: string) {
  const signupFlows = await apiFetch<SignupFlowInfo[]>(
    null,
    `corp-api/onboarding-control-panel/flow/${flowId}/signup-flows`,
  );
  return signupFlows.map((flow: SignupFlowInfo) => ({
    id: flow._id,
    name: flow.name,
    token: flow.token,
    active: flow.active,
    type: flow.type,
  }));
}

export async function getSignupFlowTags(tagIds?: string[]) {
  if (!tagIds || tagIds.length === 0) {
    return [];
  }
  const queryString = `ids=${tagIds.join(',')}`;
  const url = `corp-api/onboarding-control-panel/signup-flow/tags?${queryString}`;
  return await apiFetch<ITagJson[]>(null, url);
}

export async function getSignupFlowIntendedProjects(projectIds?: string[]) {
  if (!projectIds || projectIds.length === 0) {
    return [];
  }
  const queryString = `ids=${projectIds.join(',')}`;
  const url = `corp-api/onboarding-control-panel/signup-flow/intended-projects?${queryString}`;
  return await apiFetch<IProjectJson[]>(null, url);
}

export async function deleteSignupFlowDraftForOnboardingFlow(flowId: string): Promise<void> {
  await apiFetch<void>(null, `corp-api/onboarding-control-panel/flow/${flowId}/draft-signup-flow`, {
    method: 'DELETE',
  });
}

export function useAddUsersToFlow() {
  return useMutation<AddUserToFlowBody, AddUserToFlowErrorBody, any, any>(
    async ({
      userIds,
      flowId,
      signupFlowId,
      batchSize,
    }: {
      userIds: string[];
      flowId: string;
      signupFlowId?: string;
      batchSize: number;
    }) => {
      const batches = chunk(userIds, batchSize);
      const responses = await Promise.allSettled(
        batches.map(async userIds => {
          return await apiFetch(
            null,
            `corp-api/onboarding-control-panel/flow/${flowId}/add-users-to-flow`,
            {
              method: 'POST',
              body: {
                userIds,
                signupFlowId,
              },
            },
          );
        }),
      );

      let numSuccess = 0;
      const fulfilled: PromiseFulfilledResult<AddUserToFlowBody>[] = [];
      const rejected: PromiseRejectedResult[] = [];
      for (const response of responses) {
        if (response.status === 'rejected') {
          rejected.push(response);
        } else {
          fulfilled.push(response);
        }
      }
      fulfilled.forEach(result => {
        numSuccess += result.value.success;
      });

      if (rejected.length > 0) {
        const combinedError: AddUserToFlowErrorBody = {
          success: numSuccess,
          errorCounts: {},
          csvData: [],
        };

        rejected.forEach(result => {
          const errorBody = result.reason?.response as AddUserToFlowErrorBody;
          if (errorBody) {
            combinedError.success += errorBody.success;
            Object.entries(errorBody.errorCounts).forEach(([type, count]) => {
              combinedError.errorCounts[type as AddUserToFlowErrorType] =
                (combinedError.errorCounts[type as AddUserToFlowErrorType] || 0) + count;
            });
            combinedError.csvData.push(...errorBody.csvData);
          }
        });

        throw combinedError;
      }

      return { success: numSuccess };
    },
  );
}

interface RemoveUsersFromFlowResult {
  success: number;
  message: string;
  details: {
    removed: string[];
    notFound: string[];
    flowMismatch: string[];
    failed: Array<{ userId: string; error: string }>;
  };
}

export function useRemoveUsersFromFlow() {
  return useMutation<
    RemoveUsersFromFlowResult,
    { message: string },
    { userIds: string[]; flowId: string; reason?: string; batchSize: number }
  >(
    async ({
      userIds,
      flowId,
      reason = 'Removed via OCP UI',
      batchSize,
    }: {
      userIds: string[];
      flowId: string;
      reason?: string;
      batchSize: number;
    }) => {
      const batches = chunk(userIds, batchSize);

      const responses = await Promise.allSettled(
        batches.map(async userIds => {
          return await apiFetch<RemoveUsersFromFlowResult>(
            null,
            `corp-api/onboarding-control-panel/flow/${flowId}/remove-users-from-flow`,
            {
              method: 'POST',
              body: {
                userIds,
                reason,
              },
            },
          );
        }),
      );

      let totalSuccess = 0;
      const aggregatedDetails = {
        removed: [] as string[],
        notFound: [] as string[],
        flowMismatch: [] as string[],
        failed: [] as Array<{ userId: string; error: string }>,
      };
      const rejected: PromiseRejectedResult[] = [];

      for (const response of responses) {
        if (response.status === 'rejected') {
          rejected.push(response);
        } else {
          totalSuccess += response.value.success;
          // Aggregate details from each batch
          if (response.value.details) {
            aggregatedDetails.removed.push(...response.value.details.removed);
            aggregatedDetails.notFound.push(...response.value.details.notFound);
            aggregatedDetails.flowMismatch.push(...response.value.details.flowMismatch);
            aggregatedDetails.failed.push(...response.value.details.failed);
          }
        }
      }

      if (rejected.length > 0) {
        const firstError = rejected[0].reason?.response?.message || 'Failed to remove some users';
        throw new Error(firstError);
      }

      return {
        success: totalSuccess,
        message: `Successfully removed ${totalSuccess} users from flow`,
        details: aggregatedDetails,
      };
    },
  );
}

export function useOCPStatsStepActiveUsers(body: SimpleStepStatsRequest) {
  return useQuery(
    [`ocp-stats-step-active-user-${JSON.stringify(body)}`],
    () =>
      apiFetch<SimpleStepStatsResponse>(
        null,
        `corp-api/onboarding-control-panel/stats/step/active-users`,
        {
          method: 'POST',
          body,
        },
      ),
    { staleTime: Infinity },
  );
}

export function useOCPTransitionNumbers(flowId: string) {
  return useQuery({
    queryKey: ['onboardingFlowEvents', flowId],
    queryFn: async () => {
      return await apiFetch(
        null,
        `corp-api/onboarding-control-panel/flow/${flowId}/daily-transition-numbers`,
      );
    },
  });
}

export function useFetchPaginatedUsersOnStep(
  flowId: string,
  stepName: string,
  page: number,
  pageSize: number,
  sortOrder: 'asc' | 'desc',
) {
  return useQuery(
    [`fetch-paginated-users-on-step-${flowId}-${stepName}-${page}-${pageSize}-${sortOrder}`],
    async () => {
      const encodedStepName = encodeURIComponent(stepName);

      const queryParams = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        sortOrder,
      }).toString();

      return await apiFetch<
        {
          user_id: string;
          email: string | undefined;
          duration: string | undefined;
        }[]
      >(
        null,
        `corp-api/onboarding-control-panel/flow/${flowId}/step/${encodedStepName}/users?${queryParams}`,
      );
    },
  );
}

export function useGetAllUsersOnOCPStep(flowId: string, stepName: string, teamId?: string) {
  return useQuery(
    ['all-users-on-step', flowId, stepName, teamId],
    async () => {
      return await apiFetch<{ user_id: string; email: string | undefined }[]>(
        null,
        `corp-api/onboarding-control-panel/flow/${flowId}/${stepName}/users`,
        {
          method: 'POST',
          body: { teamId },
        },
      );
    },
    {
      enabled: !!flowId && !!stepName,
    },
  );
}

export function useGetWorkerTeamIdForCanonicalStepName(stepName: string) {
  return useQuery(
    ['canonicalWorkerTeamId', stepName],
    async () => {
      return await apiFetch<{ teamId: string }>(
        null,
        `corp-api/onboarding-control-panel/canonicalWorkerTeamId/${encodeURIComponent(stepName)}`,
      );
    },
    {
      enabled: !!stepName,
    },
  );
}

export function useOCPStatsStepConversionRate(body: SimpleStepStatsRequest) {
  return useQuery(
    [`ocp-stats-step-conversion-rate-${JSON.stringify(body)}`],
    () =>
      apiFetch<SimpleStepStatsResponse>(
        null,
        `corp-api/onboarding-control-panel/stats/step/conversion-rate`,
        {
          method: 'POST',
          body,
        },
      ),
    { staleTime: Infinity },
  );
}

export function useOCPStatsStepAverageTimeSpent(body: SimpleStepStatsRequest) {
  return useQuery(
    [`ocp-stats-step-average-time-spent-${JSON.stringify(body)}`],
    () =>
      apiFetch<SimpleStepStatsResponse>(
        null,
        `corp-api/onboarding-control-panel/stats/step/average-time-spent`,
        {
          method: 'POST',
          body,
        },
      ),
    { staleTime: Infinity },
  );
}

export function useFetchPayTags() {
  return useQuery(['payTags'], () => apiFetch<string[]>(null, `corp-api/tags/payTags`), {
    staleTime: Infinity,
  });
}

export async function fetchTags(search?: string): Promise<string[]> {
  try {
    const results = await apiFetch(null, 'corp-api/tags', {
      query: { search },
    });

    const options = results.tags.map((tag: any) => tag.name);

    return options;
  } catch (error) {
    defaultApiFetchErrorHandler(error, { handleHttp: true });
    return [];
  }
}

export const convertToOpensearchField = (
  field: ResumeQualificationSignalField | LinkedInQualificationSignalField,
): ResumeMetadataFields | null => {
  switch (field) {
    case ResumeQualificationSignalField.School:
      return ResumeMetadataFields.School;
    case ResumeQualificationSignalField.FieldOfStudy:
      return ResumeMetadataFields.FieldOfStudy;
    case ResumeQualificationSignalField.Degree:
      return ResumeMetadataFields.Degree;
    case ResumeQualificationSignalField.LanguageSpoken:
      return ResumeMetadataFields.LanguageSpoken;
    case ResumeQualificationSignalField.CompanyName:
      return ResumeMetadataFields.CompanyName;
    case ResumeQualificationSignalField.JobCountry:
      return ResumeMetadataFields.JobCountry;
    case ResumeQualificationSignalField.JobTitle:
      return ResumeMetadataFields.JobTitle;
    case ResumeQualificationSignalField.Skills:
      return ResumeMetadataFields.Skills;
    case ResumeQualificationSignalField.AccreditationName:
      return ResumeMetadataFields.AccreditationName;
    case ResumeQualificationSignalField.AccreditationOrganization:
      return ResumeMetadataFields.Organization;
    case ResumeQualificationSignalField.StandardizedTestName:
      return ResumeMetadataFields.StandardizedTestName;
    case ResumeQualificationSignalField.PublicationSource:
      return ResumeMetadataFields.PublicationSource;
    default:
      return null;
  }
};

export const useResumeMetadataValuesQuery = (
  field: ResumeQualificationSignalField | LinkedInQualificationSignalField,
  search?: string,
) =>
  useQuery(
    [field, search],
    () => {
      const opensearchField = convertToOpensearchField(field);
      if (!opensearchField) {
        return Promise.resolve({
          values: [],
          totalTerms: 0,
          totalPages: 0,
        });
      }
      return fetchPaginatedResumeMetadataValues(opensearchField, search);
    },
    {
      enabled: !!field && !!convertToOpensearchField(field),
    },
  );

export async function fetchNormalizedResumeMetadataTerms(terms: string[]): Promise<string[]> {
  try {
    const response = await apiFetch(null, '/internal/experts/resume-metadata/normalize-terms', {
      method: 'POST',
      body: { terms },
    });
    return response.normalizedTerms;
  } catch (error) {
    defaultApiFetchErrorHandler(error, { handleHttp: true });
    return [];
  }
}

export async function fetchPaginatedResumeMetadataValues(
  field: ResumeMetadataFields,
  search?: string,
  page: number = 0,
  pageSize: number = METADATA_VALUES_DISPLAY_PAGE_SIZE,
): Promise<{
  values: Array<{ value: string; count: number }>;
  totalTerms: number;
  totalPages: number;
}> {
  try {
    const { results, totalTerms } = await apiFetch(
      null,
      `corp-api/resume-search/field/${field}/values`,
      {
        query: { search, page, pageSize },
      },
    );

    return {
      values: results,
      totalTerms,
      totalPages: Math.ceil(totalTerms / pageSize),
    };
  } catch (error) {
    defaultApiFetchErrorHandler(error, { handleHttp: true });
    return { values: [], totalTerms: 0, totalPages: 0 };
  }
}

export async function getFlowStats(
  flowId: string,
  startDate: string,
  endDate: string,
  tags: string[],
) {
  const queryParams = new URLSearchParams();
  queryParams.append('startDate', startDate);
  queryParams.append('endDate', endDate);
  if (tags.length > 0) {
    queryParams.append('tags', tags.join(','));
  }

  return await apiFetch<FlowStatsResponse>(
    null,
    `corp-api/onboarding-control-panel/stats/flow/${flowId}?${queryParams}`,
    {
      method: 'GET',
    },
  );
}

export async function getStepBreakdownStats(body: SimpleStepStatsRequest) {
  return await apiFetch<StepStatsResponse>(
    null,
    `corp-api/onboarding-control-panel/stats/step/step-breakdown`,
    {
      method: 'POST',
      body,
    },
  );
}

export async function getAllVersionsConversionData(flowId: string, steps: string[]) {
  return await apiFetch(
    null,
    `corp-api/onboarding-control-panel/stats/flow/all-versions-conversion-data`,
    {
      method: 'POST',
      body: {
        flowId,
        steps,
      },
    },
  );
}

export async function fetchUserOnboardingState(userId: string) {
  return await apiFetch(null, `corp-api/onboarding-control-panel/userOnboardingState/${userId}`, {
    method: 'GET',
  });
}

export async function getStepAvgTimeSpentWithinTimeFrame(body: SimpleStepStatsRequest) {
  return await apiFetch<SimpleStepStatsResponse>(
    null,
    `corp-api/onboarding-control-panel/stats/step/average-time-spent`,
    {
      method: 'POST',
      body,
    },
  );
}

export async function getStepConversionRateWithinTimeFrame(body: SimpleStepStatsRequest) {
  return await apiFetch<SimpleStepStatsResponse>(
    null,
    `corp-api/onboarding-control-panel/stats/step/conversion-rate`,
    {
      method: 'POST',
      body,
    },
  );
}

export async function updateContributorFacingName(flowId: string, name: string) {
  return await apiFetch(null, `corp-api/onboarding-control-panel/flow/external-name`, {
    method: 'PUT',
    body: { flowId, name },
  });
}

export async function updateContributorFacingDescription(flowId: string, description: string) {
  return await apiFetch(null, `corp-api/onboarding-control-panel/flow/external-description`, {
    method: 'PUT',
    body: { flowId, description },
  });
}

export async function updatePodType(flowId: string, podType: PodType) {
  return await apiFetch(null, `corp-api/onboarding-control-panel/flow/pod-type`, {
    method: 'PUT',
    body: { flowId, podType },
  });
}

export async function createSignupFlowRequest(body: SignupFlowRequest) {
  return await apiFetch(null, `corp-api/onboarding-control-panel/signup-flow/`, {
    method: 'POST',
    body,
  });
}

export async function updateSignupFlowRequest(signupFlowId: string, body: SignupFlowRequest) {
  return await apiFetch(null, `corp-api/onboarding-control-panel/signup-flow/${signupFlowId}`, {
    method: 'PUT',
    body,
  });
}

export async function downloadUsersOnStepCSV(
  flowId: string,
  flowName: string,
  stepName: string,
  type: OnboardingFlowType,
): Promise<void> {
  try {
    // Construct the URL with the flowId and stepName
    const url = `/corp-api/onboarding-control-panel/flow/${encodeURIComponent(
      flowId,
    )}/step/${encodeURIComponent(stepName)}/users/csv?type=${encodeURIComponent(type)}`;

    const response = await apiFetch<Blob>(null, url, {
      method: 'GET',
      binary: true,
    });

    const downloadUrl = window.URL.createObjectURL(response);

    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = `${flowName}-${stepName}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    alert(`Error downloading CSV: ${error instanceof Error ? error.message : String(error)}`);
  }
}

export interface SignupFlowInfo {
  _id: string;
  token: string;
  name: string;
  onboardingFlow?: string;
  active?: boolean;
  type?: SignupFlowType;
}

export function useSignupFlowInfo(
  onboardingFlowId?: string,
  successHook = (_: SignupFlowInfo[]) => {},
) {
  return useQuery(
    [`signup-flow-info-${onboardingFlowId}`],
    async () => {
      const data = await apiFetch<SignupFlowInfo[]>(
        null,
        `corp-api/onboarding-control-panel/flow/${onboardingFlowId}/signup-flows`,
      );
      successHook(data);
      return data;
    },
    {
      enabled: !!onboardingFlowId,
    },
  );
}

interface TestFunnelWhitelistInput {
  onboardingFlowId: string;
  signupFlowId: string;
  email: string;
  deleteExisting: boolean;
}

interface TestFunnelWhitelistOutput {
  success: boolean;
  // when deleteExisting = false, returns list of dupe users if found
  existingUsers?: { _id: string; email: string }[];
}

export async function testFunnelWhitelist({
  onboardingFlowId,
  signupFlowId,
  email,
  deleteExisting,
}: TestFunnelWhitelistInput): Promise<TestFunnelWhitelistOutput> {
  const resp: Response = await apiFetch(
    null,
    `corp-api/onboarding-control-panel/flow/${onboardingFlowId}/test/whitelist`,
    {
      method: 'POST',
      returnRawResponse: true,
      body: {
        signupFlowId,
        email,
        deleteExisting,
      },
    },
  );

  if (resp.ok) {
    return { success: true };
  } else if (resp.status === 409) {
    // indicates that the user already exists
    const respBody = await resp.json();
    return {
      success: false,
      existingUsers: respBody.existingUsers,
    };
  } else {
    throw new HTTPError(resp.statusText, await tryParseErrorResponse(resp));
  }
}

async function getFlowAndPathForUserId(userId: string) {
  return await apiFetch<IUserPathData>(
    null,
    `corp-api/onboarding-control-panel/flow/userPath/${userId}`,
  );
}

async function getFlowAndPathForEmail(email: string) {
  return await apiFetch<IUserPathData>(
    null,
    `corp-api/onboarding-control-panel/flow/userPathByEmail/${encodeURIComponent(email)}`,
  );
}

export async function getBypassWhitelistEntriesForFlowAndStep(flowId: string, stepName: string) {
  return await apiFetch<string[]>(
    null,
    `corp-api/onboarding-control-panel/flow/bypassWhitelist/byFlowAndStep/${flowId}/${encodeURIComponent(
      stepName,
    )}`,
  );
}

export async function setBypassWhitelistEmailsForFlowIdAndStepName(
  flowId: string,
  stepName: string,
  emailList: string[],
  restrictToTestAccounts?: boolean,
) {
  return apiFetch(
    null,
    `corp-api/onboarding-control-panel/flow/bypassWhitelist/updateEmailListForFlowAndStep`,
    {
      method: 'POST',
      returnRawResponse: true,
      body: {
        flowId,
        stepName,
        emailList,
        restrictToTestAccounts,
      },
    },
  );
}

export async function fetchUserPathMetrics(flowId: string, userId: string) {
  return await apiFetch<IUserPathMetrics>(
    null,
    `corp-api/onboarding-control-panel/stats/flow/${flowId}/userPathMetrics/${userId}`,
  );
}

export async function fetchInterview(interviewId: string): Promise<any> {
  return await apiFetch<any>(null, `corp-api/interview/${interviewId}`);
}

export async function fetchScreening(screeningId: string): Promise<any> {
  return await apiFetch<any>(null, `corp-api/screening-center/config/${screeningId}`);
}

export async function fetchCampaignsForSignupFlow(signupFlowId: string) {
  return await apiFetch<any>(null, `corp-api/email-drip-campaign/signup-flow/${signupFlowId}`);
}

export async function fetchJobPostsForSignupFlow(signupFlowId: string) {
  return await apiFetch<JobDetailPanelData[]>(
    null,
    `corp-api/job-post/signup-flow/${signupFlowId}`,
  );
}

export const fetchSignupFlow = async (signupFlowId: string) => {
  return await apiFetch<SignupFlowResponse>(
    null,
    `corp-api/onboarding-control-panel/signup-flow/${signupFlowId}`,
  );
};

const flowSearch = async (searchText: string) => {
  const flowData = await findOCPWorkflows();
  if (isemail.validate(maybeCleanOutlierEmail(searchText), { minDomainAtoms: 2 })) {
    try {
      const userPathData = await getFlowAndPathForEmail(
        `${maybeCleanOutlierEmail(searchText)}+outlier`,
      );
      return [
        {
          id: userPathData.traversal.flowId,
          name: userPathData.flow.name,
          description: `Step: ${userPathData.traversal.currentStep}`,
        },
      ];
    } catch (error) {
      return [];
    }
  } else if (/^[0-9a-fA-F]{24}$/.test(searchText)) {
    try {
      const userPathData = await getFlowAndPathForUserId(searchText);
      return [
        {
          id: userPathData.traversal.flowId,
          name: userPathData.flow.name,
          description: `Step: ${userPathData.traversal.currentStep}`,
        },
      ];
    } catch (error) {
      return flowData
        .filter(flow => flow._id === searchText)
        .map(flow => ({ id: flow._id, name: flow.name }));
    }
  } else {
    return flowData
      .filter(flow => flow.name.toLowerCase().includes(searchText.toLowerCase()))
      .map(flow => ({ id: flow._id, name: flow.name }));
  }
};

export const queryFactory = {
  flows: {
    search: ({ searchText }: { searchText: string }) => ({
      queryKey: ['search', searchText] as const,
      queryFn: () => flowSearch(searchText),
      enabled: !!searchText,
    }),
  },
};

export const useSignupFlowNamesQuery = () =>
  useQuery<string[]>(['signupFlowNames'], async () => {
    const signupFlows = await apiFetch(null, `corp-api/onboarding-control-panel/signup-flow/`);
    return signupFlows.map((flow: SignupFlowInfo) => flow.name);
  });

export const useSignupFlowsQuery = (ocpFlowId?: string) =>
  useQuery([ocpFlowId, 'signupFlows'], () => findSignupFlowsForOnboardingFlow(ocpFlowId!), {
    enabled: !!ocpFlowId,
  });

export function useJobPostsForSignupFlowQuery(
  signupFlowId?: string,
  successHook = (_: JobDetailPanelData[]) => {},
) {
  return useQuery(
    ['jobPostsForSignupFlow', signupFlowId!],
    async () => {
      const jobPosts = await fetchJobPostsForSignupFlow(signupFlowId!);
      successHook(jobPosts);
      return jobPosts;
    },
    {
      enabled: !!signupFlowId,
    },
  );
}

export function useSignupFlowToJobPostsForOnboardingFlowQuery(ocpFlowId?: string) {
  return useQuery(
    ['jobPostsForOnboardingFlow', ocpFlowId!],
    async () => {
      const signupFlows = await findSignupFlowsForOnboardingFlow(ocpFlowId!);
      const jobPostsPromises = signupFlows.map(async flow => {
        const jobPosts = await fetchJobPostsForSignupFlow(flow.id);
        return { signupFlowName: flow.name, jobPosts };
      });
      const jobPostArrays = await Promise.all(jobPostsPromises);
      return jobPostArrays.reduce((acc, { signupFlowName, jobPosts }) => {
        acc[signupFlowName] = jobPosts;
        return acc;
      }, {} as Record<string, JobDetailPanelData[]>);
    },
    {
      enabled: !!ocpFlowId,
    },
  );
}

export const useFetchSignupFlowQuery = (signupFlowId: string) =>
  useQuery(['signupFlow', signupFlowId], () => fetchSignupFlow(signupFlowId), {
    enabled: !!signupFlowId,
  });

export const useSignupFlowWorkerSkillQuery = (selectedSignupFlow?: Partial<ISignupFlow>) =>
  useQuery<IWorkerSkill>(
    [selectedSignupFlow?.workerSkillGrant?.workerSkillId],
    () =>
      apiFetch(
        null,
        `corp-api/worker-skills/${selectedSignupFlow!.workerSkillGrant!.workerSkillId}`,
      ),
    {
      enabled: !!selectedSignupFlow?.workerSkillGrant?.workerSkillId,
    },
  );

export const useFetchFastLaneListsQuery = () =>
  useQuery<FastLaneListResponse>(['fastLaneLists'], () =>
    apiFetch(null, `corp-api/onboarding-control-panel/signup-flow/fast-lane-lists`),
  );

export const useFetchFastLaneChainQuery = (signupFlowId?: string) =>
  useQuery<FastLaneChainResponse>(
    ['fastLaneChain', signupFlowId],
    () =>
      apiFetch(
        null,
        `corp-api/onboarding-control-panel/signup-flow/fast-lane-chain/${signupFlowId}`,
      ),
    {
      enabled: !!signupFlowId,
    },
  );

export const useFetchScreeningConfigs = () =>
  useQuery<ListScreeningConfigsResponse>(['screeningConfigs'], async () => {
    return await apiFetch<ListScreeningConfigsResponse>(null, `corp-api/screening-center/config`);
  });
